import { type FC, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useOpenContractMutation } from '@/api/queries/contract';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { additionalDiscountForUsdt } from '@/common/config/common';
import { usePurchaseAttempt } from '@/common/hooks/purchase-attempt';
import { useTonAutoConnect } from '@/common/hooks/ton-auto-connect';
import { multiplication } from '@/common/lib/decimal';
import { Playdeck, SendAnalitics } from '@/common/lib/playdeck';
import {
  type ContractDataType,
  type PaidSpecContractType,
} from '@/common/types/contract';
import { ModalContext } from '@/components/Modals/Modal/config';
import { SecondaryButton } from '@/components/ui/Button/Button';
import TONandStarsBuyButtons from '@/components/ui/TONandStarsBuyButtons/TONandStarsBuyButtons';
import { removeFullscreenModal } from '@/redux/reducers/modalsReducer';
import { changePaymentStatus } from '@/redux/reducers/paymentsReducer';
import { subtractBalance } from '@/redux/reducers/userReducer';
import { useAppDispatch } from '@/redux/store';

import { PlusContractGeneralInfo } from '../PlusContractGeneralInfo/PlusContractGeneralInfo';

interface BuyProcessProps {
  contract: ContractDataType;
}

const BuyProcess: FC<BuyProcessProps> = ({ contract }) => {
  const { t } = useTranslation();
  const { secondModal, thirdModal } = useContext(ModalContext);
  const dispatch = useAppDispatch();
  const [openContract, { isLoading }] = useOpenContractMutation();
  const connectWalletAndDoTransaction = useTonAutoConnect();
  const usdPrice = useMemo(
    () =>
      multiplication(
        contract.contractCost?.amountUsd || 0,
        additionalDiscountForUsdt.coefficient,
      ),
    [contract],
  );

  usePurchaseAttempt({
    item: contract.type,
    item_id: contract.id,
    price: contract.contractCost?.amountUsd || 0,
    currency: 'usdt',
  });

  const sendAnalitics = (currency: 'ton' | 'xtr' | 'usdt'): void => {
    SendAnalitics.purchaseSuccessfull({
      item: contract.type,
      item_id: contract.id,
      price: contract.contractCost?.amountUsd || 0,
      currency,
    });
  };

  const onClick = async (currency: 'XTR' | 'TON'): Promise<void> => {
    if (!contract) {
      return;
    }

    if (currency === 'TON') {
      await new Promise<void>((resolve, reject) => {
        connectWalletAndDoTransaction(
          async () => {
            const response = await openContract({
              contractId: contract.id,
              type: contract.type,
              currency: 'ton',
            }).unwrap();

            if (response && 'address' in response) {
              return response;
            } else {
              throw new Error('Invalid response for ton transaction');
            }
          },
          {
            onSuccess: () => {
              ToastNotification(t('Pending Transaction'));
              resolve();
              dispatch(changePaymentStatus('pending'));
              sendAnalitics('ton');
            },
            onError: () => {
              ToastNotification(t('Something went wrong, please try again'));
              reject(new Error('Transaction failed'));
              dispatch(changePaymentStatus(undefined));
            },
          },
        );
      });
    } else {
      const response = await openContract({
        contractId: contract.id,
        type: contract.type,
        currency: 'xtr',
      }).unwrap();

      // for ActivateVipStatusBuyLinkResponseType response type
      if (response && 'link' in response) {
        await new Promise<void>((resolve, reject) => {
          Playdeck.requestPayment({
            amount: contract.contractCost?.amountStars || 0,
            description: 'buy arena offer',
            externalId: `buy-arena-offer-${contract.contractCost?.amountStars || 0}`,
          });
          Telegram.WebApp.openInvoice(response.link, (status) => {
            if (status === 'cancelled' || status === 'failed') {
              ToastNotification(t('Something went wrong, please try again'));
              reject(new Error(`Payment ${status}`));
            } else {
              sendAnalitics('xtr');
              resolve();
              dispatch(changePaymentStatus('pending'));
            }
            console.log(`payment status ${status}`);
          });
        });
      }
    }
  };

  const buyUsdt = async (): Promise<void> => {
    if (!contract) {
      return;
    }

    try {
      await openContract({
        contractId: contract.id,
        type: contract.type,
        currency: 'usdt',
      }).unwrap();

      if (thirdModal.isOpen) {
        thirdModal.closeModal();
      } else {
        secondModal.closeModal();
      }

      dispatch(changePaymentStatus('pending'));
      dispatch(
        subtractBalance({
          type: 'usdt',
          value: usdPrice,
        }),
      );
      sendAnalitics('usdt');
    } catch (error) {
      console.error(error);
      ToastNotification(t('Something went wrong, please try again'));
      dispatch(changePaymentStatus(undefined));
    }
  };

  return (
    <>
      <PlusContractGeneralInfo kind={contract.type as PaidSpecContractType} />
      <div className="flex flex-col w-full items-center">
        {contract.contractCost && (
          <span className="text-2xl leading-none font-extrabold mb-4">
            ${contract.contractCost?.amountUsd}
          </span>
        )}
        {contract.contractCost && (
          <TONandStarsBuyButtons
            onClick={onClick}
            onUsdtBuy={buyUsdt}
            isLoading={isLoading}
            price={contract.contractCost.amountUsd}
            discount={additionalDiscountForUsdt.percentage}
            usdPrice={usdPrice}
            fullPrice={contract.contractCost.amountUsd}
            starPrice={contract.contractCost.amountStars}
            mustBeUsdtButton={true}
            classNames="!px-0"
            variant="switch"
          />
        )}
        <SecondaryButton
          onClick={() => dispatch(removeFullscreenModal('buyPlusContract'))}
          text="Close"
          className="mt-3"
        />
      </div>
    </>
  );
};

export { BuyProcess };
